import JsBarcode from 'jsbarcode';
import { questionType, subContentType } from './enums';
import { A4Dimensions } from './constants';

export function fixNumber(number, digits = 3) {
	return number ? +number.toFixed(digits) : 0;
}

export function setDataOnDrag(event, index) {
	event.dataTransfer.setData('oldIndex', index);
	const dragImage = new Image();
	event.dataTransfer.setDragImage(dragImage, 0, 0)
}

export function getDataOnDrop(event, newIndex, array) {
	const oldIndex = +event.dataTransfer.getData('oldIndex');
	if (newIndex !== oldIndex) {
		const oldItem = array[oldIndex];
		array.splice(oldIndex, 1);
		array.splice(newIndex, 0, oldItem);
	}
}

export const generateBarcode = (selector, data, options = {}, heightInCm = 4) => {
	const PPIElement = document.createElement('div');
	PPIElement.id = 'ppi';
	PPIElement.style.width = '1in';
	PPIElement.style.visibility = 'hidden';
	PPIElement.style.position = 'absolute';
	document.body.append(PPIElement);
	const PPI = PPIElement.offsetWidth || 96;
	
	JsBarcode(selector, data, {
		fontSize: 32,
		fontOptions: 'bold',
		margin: 0,
		width: 3,
		height: Math.round(heightInCm * (PPI / 2.54)),
		// background:	'#ffffff',
		background:	'transparent',
		lineColor: '#000000',
		...options
	});

	PPIElement.remove();
}

export const generateBarcodeString = ({
	questionsCountPerPaper = '',
	questionsCountTotal = '',
	paperCount = '',
	examId = '',
	subjectId = '',
	studentId = '',
}) => {
	return [
		questionsCountPerPaper,
		questionsCountTotal,
		paperCount,
		examId,
		subjectId,
		studentId,
	].join('-');
}

const getPPI = () => {
	const PPIElement = document.createElement('div');
	PPIElement.id = 'ppi';
	PPIElement.style.width = '1in';
	PPIElement.style.visibility = 'hidden';
	PPIElement.style.position = 'absolute';
	document.body.append(PPIElement);
	const PPI = PPIElement.offsetWidth || 96;
	PPIElement.remove();
	return PPI;
}

export function convertFromPxToCm(px, dpi = 96, useAutoPPI = false) {
	const DPI = useAutoPPI ? getPPI() : dpi;
	return px / (DPI / 2.54);
}

export function convertFromCmToPx(cm, dpi = 96, useAutoPPI = false) {
	const DPI = useAutoPPI ? getPPI() : dpi;
	return cm * (DPI / 2.54);
}

export const retrieveDataFromString = (imageData) => {
	if (typeof imageData === 'string') {
		return imageData.split(';').filter(Boolean).reduce((res, a) => {
			if (a.includes(':')) {
				const data = a.split(':').map(c => c.trim());
				res[data[0]] = !isNaN(data[1]) ? +data[1] : data[1];
			}
			return res
		}, {})
	} else {
		return {}
	}
}

export const getImageOptions = (q) => {
	if (!q.questionImages.length) {
		return {
			src: null,
			preview: null,
			imageEl: null,
			imageOptions: {
				align: 'left',
				width: 0,
				height: 0,
				ratio: 0,
			}
		}
	}
	const imageOptions = retrieveDataFromString(q.questionImages[0].imageData);
	const imageObj = {
		src: null,
		preview: q.questionImages[0].imagePath,
		imageEl: new Image(q.questionImages[0].imagePath),
		imageOptions: {
			align: imageOptions.align || 'start',
			width: imageOptions.width,
			height: imageOptions.height,
			ratio:
				imageOptions.width && imageOptions.height
					? imageOptions.width / imageOptions.height
					: 1,
		}
	}
	if (!imageOptions.width && !imageOptions.height) {
		imageObj.imageEl.onload = () => {
			imageObj.imageOptions.ratio =
				imageObj.imageEl.naturalWidth / imageObj.imageEl.naturalHeight;
			const MaxWidthAllowed = fixNumber(convertFromCmToPx(A4Dimensions.CM.WIDTH), 0);
			if (imageObj.imageOptions.width > MaxWidthAllowed) {
				imageObj.imageOptions.width = MaxWidthAllowed;
				imageObj.imageOptions.height =
					fixNumber(imageObj.imageOptions.width / imageObj.imageOptions.ratio, 0);
			}
		}
	}
	return imageObj;
}

export const getSubContentType = (q) => {
	let subType;
	if (
		q.name !== 'divider' &&
		(
			(q.choices || []).length ||
			(!(q.choices || []).length && (q.childsQuestions || []).length)
		)
	) {
		subType = subContentType.question
	} else if (q.name === 'divider') {
		subType = subContentType.divider;
	} else if (q.name && q.name !== 'divider' && !(q.choices || []).length && !q.questionImages.length) {
		subType = subContentType.text;
	} else if (
		(!q.name || (q.name && q.name !== 'divider')) &&
		!q.choices.length &&
		q.questionImages.length
	) {
		subType = subContentType.image;
	}
	return subType;
}

export function refactorQuestions(questions, useContentType = false) {
	if (!useContentType) {
		return questions.map((question, i) => ({
			id: i,
			questionId: question.id,
			type: (question.choices || []).length ? questionType.single : questionType.group,
			text: question.name,
			image: question.questionImages[0] ? question.questionImages[0].imagePath : null,
			imageId: question.questionImages[0] ? question.questionImages[0].id : null,
			options: (question.choices || []).map((c, j) => ({
				id: j,
				text: c.choice,
				isCorrect: c.isCorrect
			})),
			mark: question.degree,
			subContent: (question.childsQuestions || []).map((c, j) => {
				const subType = getSubContentType(c);
				
				return {
					id: j,
					contentId: c.id,
					text: c.name,
					type: subType,
					image:
						subType === subContentType.question && c.questionImages[0]
							? c.questionImages[0].imagePath
							: null,
					imageId: c.questionImages[0] ? c.questionImages[0].id : null,
					options: c.choices.map((e, j) => ({
						id: j,
						text: e.choice,
						isCorrect: e.isCorrect
					})),
					...(subType === subContentType.image ? { ...getImageOptions(c) } : {})
				}
			})
		}))
	} else {
		return questions.map((question, i) => {
			const subType = getSubContentType(question);
			// console.log(question);
			
			return {
				id: i,
				questionId: question.id,
				subjectId: question.questionCategoryBriefResposne.chlidSujbect.id,
				type:
					(question.choices || []).length
						? questionType.single
						: (question.childsQuestions || []).length
							? questionType.group
							: undefined,
				contentType: getSubContentType(question),
				text: question.name,
				image: question.questionImages[0] ? question.questionImages[0].imagePath : null,
				imageId: question.questionImages[0] ? question.questionImages[0].id : null,
				options: (question.choices || []).map((c, j) => ({
					id: j,
					text: c.choice,
					isCorrect: c.isCorrect
				})),
				mark: question.degree,
				subContent: (question.childsQuestions || []).map((c, j) => {
					const subType = getSubContentType(c);
					
					return {
						id: j,
						contentId: c.id,
						text: c.name,
						type: subType,
						image:
							subType === subContentType.question && c.questionImages[0]
								? c.questionImages[0].imagePath
								: null,
						imageId: c.questionImages[0] ? c.questionImages[0].id : null,
						options: c.choices.map((e, j) => ({
							id: j,
							text: e.choice,
							isCorrect: e.isCorrect
						})),
						...(subType === subContentType.image ? { ...getImageOptions(c) } : {})
					}
				}),
				...(
					subType === subContentType.image
						? getImageOptions(question)
						: {}
				)
			}
		})
	}
}

export function getLateralGrade(grade) {
	if (grade === 100) return { num: 4, code: 'E' };
	if (grade >= 90) return { num: 3, code: 'VG' };
	if (grade >= 80) return { num: 2, code: 'G' };
	if (grade >= 50) return { num: 1, code: 'F' };
	return { num: 0, code: 'NI' };
}

export function generateResultOptionsData (data) {
	const flatChildren = data.flatMap(c => c.children);
	const results = flatChildren
		.filter(c => c.includeInResults)
		.map(c => {
			const percentage = fixNumber((c.numeralMark / (c.maxMark || 1)) * 100, 2);
			return {
				subject: c.subject,
				lateralMark: c.attendance
					? !c.scales.length
						? getLateralGrade(percentage || 0).code
						: c.scales.find(t => t.orderIndex === c.mark)?.code ??
							getLateralGrade(percentage || 0).code
					: '-',
				percentage: percentage + '%',
			}
		});
	
	const writingResult = flatChildren
		.filter(c => !c.isManualCorrect && c.mark !== -1)
		.reduce((res, a) => {
			res.numeralMark += a.numeralMark;
			res.maxMark += a.maxMark;
			return res;
		}, { numeralMark: 0, maxMark: 0 });
	const writingResultPercentage =
		fixNumber((writingResult.numeralMark / (writingResult.maxMark || 1)) * 100, 2);

	return {
		results,
		writingResult: {
			lateralMark: getLateralGrade(writingResultPercentage)?.code,
			percentage: writingResultPercentage + '%',
		}
	}
}